/**
 * Notification Listener Component
 * Listens for real-time notifications and displays them
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, IconButton, Badge, Button } from '@mui/material';
import { Close, Notifications, OpenInNew } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isPathAllowedForRole } from '../utils/notificationNav';
import notificationService from '../services/notificationService';

const NotificationListener = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const [currentNotification, setCurrentNotification] = useState(null);
    
    useEffect(() => {
        if (isAuthenticated && user) {
            // Connect to notification service
            notificationService.connect(user.id, user.role);
            
            // Request notification permission
            notificationService.requestNotificationPermission();
            
            // Listen for notifications
            const unsubscribeGeneral = notificationService.on('notification', (data) => {
                addNotification(data);
            });
            
            const unsubscribeNewRequest = notificationService.on('new-verification-request', (data) => {
                addNotification({
                    type: 'new-verification-request',
                    title: 'New Verification Request',
                    message: `New verification opportunity in ${data.location || 'your area'}`,
                    data
                });
            });
            
            const unsubscribeAssigned = notificationService.on('verification-assigned', (data) => {
                addNotification({
                    type: 'verification-assigned',
                    title: 'Verification Assigned',
                    message: `You have been assigned to verify: ${data.milestoneTitle || 'a project'}`,
                    data
                });
            });
            
            // Quote request notifications (for merchants)
            const unsubscribeQuoteRequest = notificationService.on('merchant-quote-request', (data) => {
                addNotification({
                    type: 'merchant-quote-request',
                    title: 'New Material Quote Request',
                    message: `You have received a new quote request for project: ${data.projectTitle || 'N/A'}`,
                    data,
                    actionUrl: `/merchant/quote-request/${data.quoteRequestId}`
                });
            });
            
            // Quote received notifications (for clients)
            const unsubscribeQuoteReceived = notificationService.on('quote-received', (data) => {
                addNotification({
                    type: 'quote-received',
                    title: 'New Quote Received',
                    message: `${data.merchantName || 'A supplier'} has submitted a quote for your request`,
                    data,
                    actionUrl: `/quotes/${data.quoteRequestId}`
                });
            });
            
            // Order notifications
            const unsubscribeOrderPlaced = notificationService.on('order-placed', (data) => {
                if (user.role === 'merchant') {
                    addNotification({
                        type: 'order-placed',
                        title: data.title || 'New Material Order',
                        message: data.message || `You have received a new material order`,
                        data,
                        actionUrl: data.data?.orderId ? `/merchants/orders/${data.data.orderId}` : '/merchants/orders'
                    });
                }
            });
            
            // Delivery notifications
            const unsubscribeDelivery = notificationService.on('delivery-submitted', (data) => {
                if (user.role === 'client') {
                    const deliveryType = data.deliveryType || (data.deliveryMethod === 'pickup' ? 'picked up' : 'delivered');
                    const title = data.title || (deliveryType === 'picked up' ? 'Order Picked Up - Action Required' : 'Materials Delivered - Action Required');
                    const message = data.message || `Your materials have been ${deliveryType}. Please confirm receipt within 72 business hours. If you do not confirm, payment will be automatically released to the merchant.`;
                    
                    addNotification({
                        type: 'delivery-submitted',
                        title: title,
                        message: message,
                        data,
                        actionUrl: data.actionUrl || `/orders/${data.orderId}/delivery/confirm`
                    });
                }
            });
            
            // Cleanup on unmount
            return () => {
                unsubscribeGeneral();
                unsubscribeNewRequest();
                unsubscribeAssigned();
                unsubscribeQuoteRequest();
                unsubscribeQuoteReceived();
                unsubscribeOrderPlaced();
                unsubscribeDelivery();
                notificationService.disconnect();
            };
        }
        // Keyed on id/role, not the whole user object, so a session refresh that
        // replaces the user reference doesn't disconnect + reconnect the socket.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?.id, user?.role]);
    
    const addNotification = (notification) => {
        const newNotification = {
            id: Date.now(),
            ...notification,
            timestamp: new Date()
        };
        
        setNotifications(prev => [newNotification, ...prev]);
        setCurrentNotification(newNotification);
        setOpen(true);
    };
    
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const handleNotificationClick = () => {
        const actionUrl = currentNotification?.actionUrl || currentNotification?.data?.actionUrl;
        if (actionUrl) {
            setOpen(false);
            // Refuse links this role can't access (e.g. a stray /admin URL sent to
            // a client) — otherwise the toast yanks the user to "Access Denied".
            if (isPathAllowedForRole(actionUrl, user?.role)) {
                navigate(actionUrl);
            } else {
                navigate('/dashboard');
            }
        }
    };
    
    const getSeverity = (type) => {
        switch (type) {
            case 'verification_assigned':
            case 'verification-assigned':
            case 'proposal_accepted':
            case 'milestone_approved':
            case 'milestone_auto_released':
            case 'withdrawal_completed':
            case 'hire_accepted':
                return 'success';
            case 'proposal_changes_requested':
            case 'bid_changes_requested':
            case 'hire_declined':
                return 'warning';
            case 'proposal_rejected':
            case 'withdrawal_failed':
            case 'error':
                return 'error';
            case 'new-verification-request':
                return 'info';
            default:
                return 'info';
        }
    };
    
    return (
        <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Alert
                onClose={handleClose}
                severity={getSeverity(currentNotification?.type)}
                sx={{
                    width: '100%',
                    minWidth: 300,
                    cursor: (currentNotification?.actionUrl || currentNotification?.data?.actionUrl) ? 'pointer' : 'default',
                }}
                icon={<Notifications />}
                onClick={handleNotificationClick}
                action={
                    <>
                        {(currentNotification?.actionUrl || currentNotification?.data?.actionUrl) && (
                            <Button
                                size="small"
                                color="inherit"
                                onClick={(e) => { e.stopPropagation(); handleNotificationClick(); }}
                                sx={{ mr: 1 }}
                            >
                                View
                            </Button>
                        )}
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={(e) => { e.stopPropagation(); handleClose(e); }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </>
                }
            >
                <strong>{currentNotification?.title}</strong>
                <br />
                {currentNotification?.message}
            </Alert>
        </Snackbar>
    );
};

export default NotificationListener;

